import React, { useState, useEffect, useCallback } from 'react';
import { CONTACT_EMAIL } from './contact.js';
import { navigate } from './site/routes.js';

// Event galleries — free portraits from events we cover.
//
// Same chrome as the Paws gallery so a visitor crossing from one to the other
// sees one studio, but a different job: nothing is for sale here. Each person
// finds their portrait, downloads it, and is offered the newsletter in one
// line of small type — ticked by default, one tap to decline.

const API_BASE = import.meta.env.VITE_API_URL || '';

const mono = `'Space Mono', 'Courier Prime', 'Courier New', monospace`;
const sans = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;

const c = {
  cream: '#fdf9f3',
  paper: '#ffffff',
  sand: '#e0d3c2',
  orange: '#b8560f',
  accent: '#d97540',
  terracotta: '#a84032',
  brown: '#2b1810',
  ink: '#2b1810',
  muted: '#6b5648',
  ground: '#2b1810',
  onGround: '#fdf9f3',
  groundMuted: '#b8a495',
  groundLine: '#4a2e1f',
};

const RESPONSIVE_CSS = `
.eg-layout { max-width:1160px; margin:0 auto; padding:56px 24px 72px; display:grid;
  grid-template-columns:minmax(0,1fr) 260px; gap:56px; align-items:start; }
.eg-cards { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:28px; }
.eg-detail { max-width:820px; margin:0 auto; padding:48px 24px 72px; }
.eg-bar { max-width:1160px; margin:0 auto; padding:0 24px; display:flex; align-items:center; min-height:62px; }
.eg-mast { padding:52px 24px 36px; }
.eg-mast-title { font-size:52px; }
.eg-foot { max-width:1160px; margin:0 auto; padding:48px 24px 28px; display:grid;
  grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:28px; }
.eg-name { font-size:44px; }
@media (max-width: 900px) {
  .eg-layout { grid-template-columns:1fr; gap:40px; padding:40px 18px 56px; }
}
@media (max-width: 620px) {
  .eg-cards { grid-template-columns:repeat(2,1fr); gap:14px; }
  .eg-detail { padding:32px 18px 56px; }
  .eg-mast { padding:36px 18px 26px; }
  .eg-mast-title { font-size:32px; }
  .eg-bar { padding:0 18px; }
  .eg-foot { padding:36px 18px 24px; }
  .eg-name { font-size:30px; }
}
`;

const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const stopIfModified = (ev) => ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button === 1;
const link = (href) => (ev) => {
  if (stopIfModified(ev)) return;
  ev.preventDefault();
  navigate(href);
};

// ---------------------------------------------------------------------------
// Shared chrome
// ---------------------------------------------------------------------------

function Masthead({ title, subtitle }) {
  return (
    <header>
      <style dangerouslySetInnerHTML={{ __html: RESPONSIVE_CSS }} />
      <div style={{ background: c.ground, color: c.onGround, borderBottom: `2px solid ${c.accent}` }}>
        <div className="eg-bar">
          <a href="/" onClick={link('/')} aria-label="HDDN LAB — home"
            style={{ display: 'flex', alignItems: 'baseline', gap: 8, textDecoration: 'none', color: c.onGround }}>
            <b style={{ fontFamily: sans, fontWeight: 800, fontSize: 17, letterSpacing: '0.16em', textTransform: 'uppercase' }}>HDDN LAB</b>
            <i style={{ fontStyle: 'normal', fontFamily: mono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: c.accent }}>New Rochelle</i>
          </a>
        </div>
      </div>
      <div className="eg-mast" style={{ background: c.cream, textAlign: 'center', borderBottom: `1px solid ${c.sand}` }}>
        <h1 className="eg-mast-title" style={{ fontFamily: sans, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02, color: c.ink, margin: 0 }}>
          {title || 'Event photos'}
        </h1>
        {subtitle && (
          <p style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: c.orange, margin: '16px 0 0' }}>{subtitle}</p>
        )}
      </div>
    </header>
  );
}

function Footer() {
  const cols = [
    { head: 'Hire us', links: [['Event coverage', '/photographer'], ['Content creation', '/content'], ['The work', '/work']] },
    { head: 'Book the room', links: [['The studio & rates', '/studio'], ['Availability', '/book']] },
  ];
  return (
    <footer style={{ background: c.ground, color: c.onGround }}>
      <div className="eg-foot">
        <div>
          <p style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Just Create</p>
          <address style={{ fontStyle: 'normal', fontFamily: sans, marginTop: 14, color: c.groundMuted, fontSize: 13, lineHeight: 1.7 }}>
            72 Centre Avenue<br />New Rochelle, NY 10801<br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.accent, textDecoration: 'none' }}>{CONTACT_EMAIL}</a>
          </address>
        </div>
        {cols.map((col) => (
          <div key={col.head}>
            <p style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: c.accent, margin: 0 }}>{col.head}</p>
            <ul style={{ listStyle: 'none', margin: '14px 0 0', padding: 0, display: 'grid', gap: 9 }}>
              {col.links.map(([label, href]) => (
                <li key={href}>
                  <a href={href} onClick={link(href)} style={{ fontFamily: sans, fontSize: 13, color: c.onGround, textDecoration: 'none' }}>{label}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="eg-foot" style={{ display: 'block', paddingTop: 0 }}>
        <div style={{ borderTop: `1px solid ${c.groundLine}`, paddingTop: 20, fontFamily: mono, fontSize: 11, letterSpacing: '0.08em', color: c.groundMuted }}>
          © {new Date().getFullYear()} HDDN LAB · 8 AM – 8 PM, seven days
        </div>
      </div>
    </footer>
  );
}

function Loading({ label = 'Loading…' }) {
  return (
    <div style={{ fontFamily: mono, fontSize: 12, letterSpacing: 2, color: c.muted, textAlign: 'center', padding: '120px 24px' }}>
      {label}
    </div>
  );
}

function Problem({ title, message }) {
  return (
    <>
      <Masthead title={title} />
      <div style={{ fontFamily: sans, color: c.terracotta, textAlign: 'center', padding: '100px 24px' }}>{message}</div>
    </>
  );
}

function usePublicJson(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    fetch(`${API_BASE}${path}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'We couldn’t load that.');
        return d;
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [path]);
  useEffect(() => { load(); }, [load]);
  return { data, error };
}

// The one ask. A single field in the sidebar or under the photo; no modal, no
// timer, no pre-ticked box. `email` pre-fills from the download field so a
// visitor who already typed it only has to agree.
function SubscribeStrip({ event, photo, email: initial = '', compact = false, onDone }) {
  const [email, setEmail] = useState(initial);
  const [state, setState] = useState('idle'); // idle | busy | done | error
  const [msg, setMsg] = useState('');
  useEffect(() => { if (initial && !email) setEmail(initial); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const v = email.trim();
    if (!EMAIL_OK.test(v)) { setState('error'); setMsg('Please enter a valid email address.'); return; }
    setState('busy'); setMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/event-galleries/${encodeURIComponent(event)}/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: v, photo: photo || null }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'That didn’t go through.');
      setState('done');
      if (onDone) onDone(v);
    } catch (e) { setState('error'); setMsg(e.message); }
  };

  if (state === 'done') {
    return (
      <p style={{ fontFamily: sans, fontSize: 13, color: c.orange, margin: 0, lineHeight: 1.8 }}>
        You’re on the list. We’ll write when there’s something worth seeing.
      </p>
    );
  }
  return (
    <div>
      {!compact && (
        <p style={{ fontFamily: sans, fontSize: 13, color: c.brown, lineHeight: 1.9, margin: '0 0 12px' }}>
          Get updates from the HDDN LAB newsletter — new photos from around New Rochelle and what we’re working on, now and then.
        </p>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="email" value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="your@email.com"
          style={{ flex: '1 1 160px', minWidth: 0, boxSizing: 'border-box', padding: '11px 12px', fontFamily: sans, fontSize: 14, color: c.ink, background: c.paper, border: `1px solid ${state === 'error' ? c.terracotta : c.sand}` }}
        />
        <button type="button" onClick={submit} disabled={state === 'busy'}
          style={{ background: 'none', color: c.brown, border: `1px solid ${c.brown}`, cursor: state === 'busy' ? 'default' : 'pointer', fontFamily: mono, fontSize: 10, letterSpacing: 2, padding: '11px 16px' }}>
          {state === 'busy' ? '…' : 'SUBSCRIBE'}
        </button>
      </div>
      {msg && <p style={{ fontFamily: sans, fontSize: 12, color: c.terracotta, margin: '8px 0 0' }}>{msg}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Index — everyone from the event
// ---------------------------------------------------------------------------

function GalleryIndex({ event }) {
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const key = search.get('k') || '';
  const { data, error } = usePublicJson(`/api/event-galleries/${encodeURIComponent(event)}${key ? `?k=${encodeURIComponent(key)}` : ''}`);
  const [q, setQ] = useState('');
  const subscribed = search.get('subscribed') === '1';

  if (error) return <Problem message={error} />;
  if (!data) return <><Masthead /><Loading /></>;

  const portraits = data.kind === 'portraits';
  // Coverage of a night has no names to search; the box only earns its place
  // when at least some frames carry one.
  const searchable = portraits || data.entries.some((e) => e.name);
  const term = q.trim().toLowerCase();
  const entries = term
    ? data.entries.filter((e) => (e.name || '').toLowerCase().includes(term))
    : data.entries;
  const subtitle = [data.eventDate, 'New Rochelle, NY'].filter(Boolean).join(' · ');

  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead title={data.title} subtitle={subtitle} />

      <div className="eg-layout">
        <main>
          {subscribed && (
            <p style={{ fontFamily: sans, fontSize: 13, color: c.orange, margin: '0 0 24px' }}>You’re on the list — thank you.</p>
          )}
          {data.keyed && !subscribed && (
            <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, margin: '0 0 24px', lineHeight: 1.7 }}>
              {data.keyedName ? `Hi ${data.keyedName} — ` : ''}open any frame and the download is one tap.
            </p>
          )}
          {(data.bookingOpen || data.signupOpen) && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
              {data.bookingOpen && (
                <a href={`/events/${encodeURIComponent(data.slug)}/book`} onClick={link(`/events/${encodeURIComponent(data.slug)}/book`)}
                  style={{ background: c.orange, color: c.cream, textDecoration: 'none', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '15px 28px' }}>
                  BOOK A SLOT{data.priceCents ? ` · $${Math.round(data.priceCents / 100)}` : ''}
                </a>
              )}
              {data.signupOpen && (
                <a href={`/events/${encodeURIComponent(data.slug)}/signup`} onClick={link(`/events/${encodeURIComponent(data.slug)}/signup`)}
                  style={{ background: 'none', color: c.brown, border: `1px solid ${c.brown}`, textDecoration: 'none', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '14px 28px' }}>
                  SIGN UP
                </a>
              )}
            </div>
          )}
          {!data.entries.length && (
            <p style={{ fontFamily: sans, fontSize: 14, color: c.muted, lineHeight: 1.9 }}>
              {data.bookingOpen || data.signupOpen ? 'Photos from the event will appear here afterwards.' : 'The photos go live soon — check back shortly.'}
            </p>
          )}
          {!!data.entries.length && !entries.length && (
            <p style={{ fontFamily: sans, fontSize: 14, color: c.muted }}>Nothing here matches “{q}”.</p>
          )}
          <div className="eg-cards">
            {entries.map((e) => <Card key={e.slug} entry={e} keyParam={data.keyed ? key : ''} />)}
          </div>
        </main>

        <aside>
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontFamily: sans, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: c.ink, marginBottom: 10 }}>
              {portraits ? 'Find yourself' : 'The photos'}
            </div>
            <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.9, margin: searchable ? '0 0 14px' : 0 }}>
              {data.blurb || (portraits
                ? `Everyone who sat for a portrait at ${data.title} is here. Open yours to download the full-size file — it’s free.`
                : `${data.title}, photographed by HDDN LAB. Open any frame to download it full size — it’s free.`)}
            </p>
            {searchable && (
              <input
                value={q}
                onChange={(ev) => setQ(ev.target.value)}
                placeholder="Search by name"
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontFamily: sans, fontSize: 14, color: c.ink, background: c.cream, border: `1px solid ${c.sand}` }}
              />
            )}
          </div>

          {data.hasFullSet && <FullSetDownload data={data} keyParam={data.keyed ? key : ''} />}

          <div style={{ borderTop: `1px solid ${c.sand}`, paddingTop: 22, marginBottom: 28 }}>
            <SubscribeStrip event={data.slug} />
          </div>

          <p style={{ fontFamily: sans, fontSize: 13, color: c.muted, lineHeight: 1.9, margin: 0 }}>
            {portraits ? 'Don’t see yourself, or the name’s wrong?' : 'In a photo you’d rather not have up?'} Write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a> and we’ll {portraits ? 'fix it' : 'take it down'}.
          </p>
          {data.credit && (
            <p style={{ fontFamily: mono, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: c.muted, lineHeight: 1.9, margin: '18px 0 0' }}>
              {data.credit}
            </p>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}

// Party galleries: most people want everything, not one frame. Same email
// and the same opt-in box as a single download, then straight to the folder.
function FullSetDownload({ data, keyParam = '' }) {
  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [err, setErr] = useState('');
  const go = () => {
    const params = new URLSearchParams();
    if (keyParam) {
      params.set('k', keyParam);
    } else {
      const v = email.trim();
      if (!EMAIL_OK.test(v)) { setErr('Please enter a valid email address.'); return; }
      params.set('email', v);
    }
    if (optIn) params.set('subscribe', '1');
    window.open(`${API_BASE}${data.fullSetUrl}?${params}`, '_blank', 'noopener');
  };
  return (
    <div style={{ border: `2px solid ${c.orange}`, background: c.cream, padding: '20px 18px', marginBottom: 28 }}>
      <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 8 }}>The whole set</div>
      <p style={{ fontFamily: sans, fontSize: 13, color: c.brown, lineHeight: 1.8, margin: '0 0 12px' }}>
        Every photo from the event, full size, free.{keyParam ? '' : ' Drop your email and we’ll open the folder.'}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {!keyParam && <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') go(); }} placeholder="your@email.com"
          style={{ flex: '1 1 150px', minWidth: 0, boxSizing: 'border-box', padding: '11px 12px', fontFamily: sans, fontSize: 14, color: c.ink, background: c.paper, border: `1px solid ${err ? c.terracotta : c.sand}` }} />}
        <button type="button" onClick={go}
          style={{ background: c.orange, color: c.cream, border: 'none', cursor: 'pointer', fontFamily: mono, fontSize: 10, letterSpacing: 2, padding: '11px 16px' }}>
          OPEN FOLDER
        </button>
      </div>
      {err && <p style={{ fontFamily: sans, fontSize: 12, color: c.terracotta, margin: '8px 0 0' }}>{err}</p>}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, fontFamily: sans, fontSize: 12, color: c.brown, lineHeight: 1.6, cursor: 'pointer' }}>
        <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} style={{ marginTop: 2 }} />
        <span>I’d like to receive updates from the HDDN LAB newsletter.</span>
      </label>
    </div>
  );
}

function Card({ entry, keyParam = '' }) {
  const href = keyParam ? `${entry.pageUrl}?k=${encodeURIComponent(keyParam)}` : entry.pageUrl;
  return (
    <article style={{ border: `1px solid ${c.sand}`, background: c.paper, display: 'flex', flexDirection: 'column' }}>
      <a href={href} onClick={link(href)} style={{ display: 'block' }}>
        <img
          src={`${API_BASE}${entry.heroUrl}?w=800`}
          alt={entry.name || 'Portrait'}
          loading="lazy"
          style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', display: 'block' }}
        />
      </a>
      <div style={{ padding: '14px 14px 16px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {entry.name && (
          <a href={href} onClick={link(href)} style={{ textDecoration: 'none' }}>
            <h2 style={{ fontFamily: sans, fontSize: 16, color: c.ink, margin: 0, fontWeight: 700, letterSpacing: '-0.02em' }}>
              {entry.name}
            </h2>
          </a>
        )}
        <a href={href} onClick={link(href)}
          style={{ alignSelf: 'center', color: c.orange, textDecoration: 'none', fontFamily: mono, fontSize: 10, letterSpacing: 2 }}>
          DOWNLOAD →
        </a>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Detail — one person, free download
// ---------------------------------------------------------------------------

function GalleryEntry({ event, slug }) {
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const key = search.get('k') || '';
  const { data, error } = usePublicJson(
    `/api/event-galleries/${encodeURIComponent(event)}/photos/${encodeURIComponent(slug)}${key ? `?k=${encodeURIComponent(key)}` : ''}`
  );
  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(true);
  const [dlError, setDlError] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const [subscribedNow, setSubscribedNow] = useState(search.get('subscribed') === '1');

  if (error && !data) return <Problem message={error} />;
  if (!data) return <><Masthead /><Loading /></>;

  const g = data.gallery;
  const portraits = g.kind === 'portraits';
  const keyed = !!data.keyed;
  const indexHref = `/events/${encodeURIComponent(g.slug)}${keyed ? `?k=${encodeURIComponent(key)}` : ''}`;

  const download = () => {
    const params = new URLSearchParams();
    if (keyed) {
      params.set('k', key);
    } else {
      const v = email.trim();
      if (!EMAIL_OK.test(v)) { setDlError('Please enter a valid email address.'); return; }
      params.set('email', v);
    }
    if (optIn) params.set('subscribe', '1');
    setDownloaded(true);
    if (optIn) setSubscribedNow(true);
    window.location.href = `${API_BASE}${data.downloadUrl}?${params.toString()}`;
  };

  const btn = {
    background: c.orange, color: c.cream, border: 'none', cursor: 'pointer',
    fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '15px 28px',
  };

  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead title={g.title} subtitle={g.eventDate || undefined} />

      <div className="eg-detail">
        <a href={indexHref} onClick={link(indexHref)} style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: c.muted, textDecoration: 'none' }}>
          {portraits ? '← EVERYONE FROM THE EVENT' : '← ALL PHOTOS'}
        </a>

        {data.name && (
          <h2 className="eg-name" style={{ fontFamily: sans, color: c.ink, margin: '24px 0 8px', fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05, textAlign: 'center' }}>
            {data.name}
          </h2>
        )}

        <img
          src={`${API_BASE}${data.heroUrl}?w=1600`}
          alt={data.name || 'Portrait'}
          style={{ width: '100%', display: 'block', margin: '28px 0' }}
        />

        {data.caption && (
          <p style={{ fontFamily: sans, fontSize: 16, color: c.brown, lineHeight: 1.75, textAlign: 'center', margin: '0 0 36px' }}>
            {data.caption}
          </p>
        )}

        {/* The download. Full size, free, no account. */}
        <div style={{ border: `2px solid ${c.orange}`, background: c.cream, padding: '30px 24px', textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 10 }}>
            {portraits || keyed ? 'Your photo' : 'This photo'}
          </div>
          <p style={{ fontFamily: sans, fontSize: 15, color: c.brown, lineHeight: 1.8, margin: '0 0 20px' }}>
            {keyed
              ? 'Full resolution, free, yours to keep. One tap.'
              : 'Full resolution, free, yours to keep. Drop your email so we know whose it is.'}
          </p>

          {keyed ? (
            <button onClick={download} style={btn}>DOWNLOAD YOUR PHOTO</button>
          ) : (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', maxWidth: 480, margin: '0 auto' }}>
              <input
                type="email" value={email}
                onChange={(e) => { setEmail(e.target.value); setDlError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') download(); }}
                placeholder="your@email.com"
                style={{ flex: '1 1 200px', minWidth: 0, boxSizing: 'border-box', padding: '13px 12px', fontFamily: sans, fontSize: 14, color: c.ink, background: c.paper, border: `1px solid ${dlError ? c.terracotta : c.sand}` }}
              />
              <button onClick={download} style={btn}>DOWNLOAD</button>
            </div>
          )}
          {dlError && <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '10px 0 0' }}>{dlError}</p>}

          {/* The ask — one line, ticked by default; one tap to decline. */}
          {!subscribedNow && (
            <label style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8, marginTop: 16, fontFamily: sans, fontSize: 13, color: c.brown, lineHeight: 1.6, cursor: 'pointer', textAlign: 'left', maxWidth: 440 }}>
              <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} style={{ marginTop: 3 }} />
              <span>I’d like to receive updates from the HDDN LAB newsletter.</span>
            </label>
          )}
          {subscribedNow && (
            <p style={{ fontFamily: sans, fontSize: 13, color: c.orange, margin: '16px 0 0' }}>You’re on the list — thank you.</p>
          )}
          {downloaded && (
            <p style={{ fontFamily: sans, fontSize: 12, color: c.muted, margin: '14px 0 0', lineHeight: 1.7 }}>
              Your download should have started. If it didn’t, tap the button again, or email{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a>.
            </p>
          )}
        </div>

        {g.credit && (
          <p style={{ fontFamily: mono, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: c.muted, textAlign: 'center', margin: '0 0 8px' }}>
            {g.credit}
          </p>
        )}
        <p style={{ fontFamily: sans, fontSize: 13, color: c.muted, lineHeight: 1.9, textAlign: 'center', margin: 0 }}>
          {portraits ? 'Wrong name, or want it taken down?' : 'Want this one taken down?'} Email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a>.
        </p>
      </div>

      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sign-up sheet — the clipboard at the door
// ---------------------------------------------------------------------------

function SignupPage({ event }) {
  const { data, error } = usePublicJson(`/api/event-galleries/${encodeURIComponent(event)}`);
  const [form, setForm] = useState({ name: '', email: '', note: '', subscribe: true });
  const [state, setState] = useState('idle'); // idle | busy | done | error
  const [msg, setMsg] = useState('');
  const [first, setFirst] = useState('');

  if (error) return <Problem message={error} />;
  if (!data) return <><Masthead /><Loading /></>;
  if (!data.signupOpen) return <Problem title={data.title} message="Sign-ups aren’t open for this event." />;

  const submit = async () => {
    if (!form.name.trim()) { setState('error'); setMsg('Please enter your name.'); return; }
    if (!EMAIL_OK.test(form.email.trim())) { setState('error'); setMsg('Please enter a valid email address.'); return; }
    setState('busy'); setMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/event-galleries/${encodeURIComponent(event)}/signup`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'That didn’t go through.');
      setFirst(d.first || ''); setState('done');
    } catch (e) { setState('error'); setMsg(e.message); }
  };
  const field = { width: '100%', boxSizing: 'border-box', padding: '13px 12px', fontFamily: sans, fontSize: 16, color: c.ink, background: c.paper, border: `1px solid ${c.sand}`, marginBottom: 12 };
  const indexHref = `/events/${encodeURIComponent(data.slug)}`;

  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead title={data.title} subtitle={data.eventDate || undefined} />
      <div className="eg-detail" style={{ maxWidth: 520 }}>
        {state === 'done' ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <h2 style={{ fontFamily: sans, fontWeight: 800, fontSize: 34, letterSpacing: '-0.03em', color: c.ink, margin: '0 0 12px' }}>You’re on the list{first ? `, ${first}` : ''}.</h2>
            <p style={{ fontFamily: sans, fontSize: 15, color: c.brown, lineHeight: 1.8, margin: 0 }}>
              We’ll email you when the photos are up.
            </p>
            <button type="button" onClick={() => { setForm({ name: '', email: '', note: '', subscribe: true }); setState('idle'); }}
              style={{ marginTop: 28, background: 'none', border: `1px solid ${c.sand}`, color: c.muted, cursor: 'pointer', fontFamily: mono, fontSize: 10, letterSpacing: 2, padding: '10px 16px' }}>
              SIGN UP SOMEONE ELSE
            </button>
          </div>
        ) : (
          <>
            <h2 style={{ fontFamily: sans, fontWeight: 800, fontSize: 34, letterSpacing: '-0.03em', color: c.ink, margin: '24px 0 10px' }}>Leave your name.</h2>
            <p style={{ fontFamily: sans, fontSize: 15, color: c.brown, lineHeight: 1.8, margin: '0 0 24px' }}>
              {data.blurb || 'We’ll email you when the photos are up — full resolution, free.'}
            </p>
            <input style={field} placeholder="Name" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input style={field} type="email" placeholder="Email" autoComplete="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontFamily: sans, fontSize: 13, color: c.brown, lineHeight: 1.6, cursor: 'pointer', marginBottom: 20 }}>
              <input type="checkbox" checked={form.subscribe} onChange={(e) => setForm({ ...form, subscribe: e.target.checked })} style={{ marginTop: 3 }} />
              <span>I’d like to receive updates from the HDDN LAB newsletter.</span>
            </label>
            {msg && <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '0 0 12px' }}>{msg}</p>}
            <button type="button" onClick={submit} disabled={state === 'busy'}
              style={{ width: '100%', background: c.orange, color: c.cream, border: 'none', cursor: 'pointer', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '16px' }}>
              {state === 'busy' ? 'SAVING…' : 'SIGN UP'}
            </button>
            <p style={{ fontFamily: sans, fontSize: 12, color: c.muted, textAlign: 'center', margin: '18px 0 0' }}>
              <a href={indexHref} onClick={link(indexHref)} style={{ color: c.muted }}>See the gallery</a>
            </p>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Booking — pick a slot, pay, done
// ---------------------------------------------------------------------------

function BookPage({ event }) {
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const bookedToken = search.get('booked');
  const cancelled = search.get('cancelled') === '1';
  const { data, error } = usePublicJson(`/api/event-galleries/${encodeURIComponent(event)}/slots`);
  const [slotId, setSlotId] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', subscribe: true });
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const [confirmed, setConfirmed] = useState(null);

  // After Stripe: read the booking until the webhook flips it to confirmed.
  useEffect(() => {
    if (!bookedToken) return undefined;
    let tries = 0, timer;
    const poll = async () => {
      const r = await fetch(`${API_BASE}/api/event-galleries/${encodeURIComponent(event)}/booking/${encodeURIComponent(bookedToken)}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) setConfirmed(d);
      if (r.ok && d.status === 'confirmed') return;
      if (tries++ < 8) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [bookedToken, event]);

  if (error) return <Problem message={error} />;
  if (!data) return <><Masthead /><Loading /></>;

  const indexHref = `/events/${encodeURIComponent(event)}`;
  const money = (cents) => `$${Number.isInteger(cents / 100) ? cents / 100 : (cents / 100).toFixed(2)}`;

  // Group slots by day for the picker.
  const days = [];
  for (const s of data.slots) {
    const day = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(s.startsAt));
    let d = days.find((x) => x.day === day);
    if (!d) { d = { day, slots: [] }; days.push(d); }
    d.slots.push(s);
  }
  const time = (iso) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }).format(new Date(iso));

  const submit = async () => {
    if (!slotId) { setState('error'); setMsg('Pick a time.'); return; }
    if (!form.name.trim()) { setState('error'); setMsg('Please enter your name.'); return; }
    if (!EMAIL_OK.test(form.email.trim())) { setState('error'); setMsg('Please enter a valid email address.'); return; }
    setState('busy'); setMsg('');
    try {
      const r = await fetch(`${API_BASE}/api/event-galleries/${encodeURIComponent(event)}/book`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, slotId }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'That didn’t go through.');
      if (d.url) { window.location.href = d.url; return; }
      window.history.replaceState({}, '', `${indexHref}/book?booked=${encodeURIComponent(d.token)}`);
      const c2 = await fetch(`${API_BASE}/api/event-galleries/${encodeURIComponent(event)}/booking/${encodeURIComponent(d.token)}`);
      setConfirmed(await c2.json());
      setState('idle');
    } catch (e) { setState('error'); setMsg(e.message); }
  };
  const field = { width: '100%', boxSizing: 'border-box', padding: '13px 12px', fontFamily: sans, fontSize: 16, color: c.ink, background: c.paper, border: `1px solid ${c.sand}`, marginBottom: 12 };

  if (confirmed) {
    const paidWait = confirmed.status === 'pending';
    return (
      <div style={{ background: c.paper, minHeight: '100vh' }}>
        <Masthead title={data.title} />
        <div className="eg-detail" style={{ maxWidth: 520, textAlign: 'center' }}>
          <h2 style={{ fontFamily: sans, fontWeight: 800, fontSize: 34, letterSpacing: '-0.03em', color: c.ink, margin: '40px 0 12px' }}>
            {paidWait ? 'Finishing up…' : `You’re booked${confirmed.name ? `, ${confirmed.name.split(' ')[0]}` : ''}.`}
          </h2>
          <p style={{ fontFamily: sans, fontSize: 16, color: c.brown, lineHeight: 1.8, margin: '0 0 8px' }}>{confirmed.when}</p>
          <p style={{ fontFamily: sans, fontSize: 14, color: c.muted, lineHeight: 1.8, margin: '0 0 28px' }}>{confirmed.location}</p>
          {paidWait ? (
            <p style={{ fontFamily: sans, fontSize: 13, color: c.muted }}>Payment received — confirming your slot, this page will update in a moment.</p>
          ) : (
            <>
              <p style={{ fontFamily: sans, fontSize: 14, color: c.brown, lineHeight: 1.8, margin: '0 0 20px' }}>A confirmation is on its way to {confirmed.email}.</p>
              <a href={`${API_BASE}${confirmed.icsUrl}`} style={{ display: 'inline-block', background: c.brown, color: c.cream, textDecoration: 'none', fontFamily: mono, fontSize: 10, letterSpacing: 2, padding: '12px 20px' }}>ADD TO CALENDAR</a>
            </>
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead title={data.title} subtitle={data.location || undefined} />
      <div className="eg-detail" style={{ maxWidth: 620 }}>
        <a href={indexHref} onClick={link(indexHref)} style={{ fontFamily: mono, fontSize: 11, letterSpacing: 2, color: c.muted, textDecoration: 'none' }}>← BACK</a>
        <h2 style={{ fontFamily: sans, fontWeight: 800, fontSize: 34, letterSpacing: '-0.03em', color: c.ink, margin: '24px 0 10px' }}>
          Pick a time{data.priceCents ? ` · ${money(data.priceCents)}` : ''}
        </h2>
        {data.blurb && <p style={{ fontFamily: sans, fontSize: 15, color: c.brown, lineHeight: 1.8, margin: '0 0 24px' }}>{data.blurb}</p>}
        {cancelled && <p style={{ fontFamily: sans, fontSize: 13, color: c.muted, margin: '0 0 16px' }}>No charge was made. Pick a time whenever you’re ready.</p>}
        {!days.length && <p style={{ fontFamily: sans, fontSize: 14, color: c.muted }}>Every slot is taken. Email <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: c.orange }}>{CONTACT_EMAIL}</a> and we’ll see what we can do.</p>}
        {days.map((d) => (
          <div key={d.day} style={{ marginBottom: 22 }}>
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.muted, marginBottom: 10 }}>{d.day}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {d.slots.map((s) => {
                const full = s.remaining <= 0, on = slotId === s.id;
                return (
                  <button key={s.id} type="button" disabled={full} onClick={() => setSlotId(s.id)}
                    style={{ fontFamily: mono, fontSize: 12, letterSpacing: 1, padding: '10px 14px', cursor: full ? 'default' : 'pointer',
                      background: on ? c.orange : c.paper, color: full ? c.sand : on ? c.cream : c.brown, border: `1px solid ${on ? c.orange : c.sand}`, textDecoration: full ? 'line-through' : 'none' }}>
                    {time(s.startsAt)}{!full && s.capacity > 1 && s.remaining <= 2 ? ` · ${s.remaining} left` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {!!days.length && (
          <div style={{ borderTop: `1px solid ${c.sand}`, paddingTop: 22, marginTop: 8 }}>
            <input style={field} placeholder="Name" autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input style={field} type="email" placeholder="Email" autoComplete="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input style={field} type="tel" placeholder="Phone (optional)" autoComplete="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontFamily: sans, fontSize: 13, color: c.brown, lineHeight: 1.6, cursor: 'pointer', marginBottom: 20 }}>
              <input type="checkbox" checked={form.subscribe} onChange={(e) => setForm({ ...form, subscribe: e.target.checked })} style={{ marginTop: 3 }} />
              <span>I’d like to receive updates from the HDDN LAB newsletter.</span>
            </label>
            {msg && <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '0 0 12px' }}>{msg}</p>}
            {!data.stripe && <p style={{ fontFamily: sans, fontSize: 13, color: c.terracotta, margin: '0 0 12px' }}>Card payments aren’t set up yet — email {CONTACT_EMAIL} to book.</p>}
            <button type="button" onClick={submit} disabled={state === 'busy' || !data.stripe}
              style={{ width: '100%', background: c.orange, color: c.cream, border: 'none', cursor: 'pointer', fontFamily: mono, fontSize: 11, letterSpacing: 2, padding: '16px' }}>
              {state === 'busy' ? 'ONE MOMENT…' : data.priceCents ? `CONTINUE TO PAYMENT · ${money(data.priceCents)}` : 'BOOK IT'}
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// /events — every gallery and open event, Paws & Portraits among them
// ---------------------------------------------------------------------------

function EventsIndex() {
  const { data, error } = usePublicJson('/api/event-galleries');
  const { data: paws } = usePublicJson('/api/paws-gallery/portfolio');
  if (error) return <Problem title="Events" message={error} />;
  if (!data) return <><Masthead title="Events" /><Loading /></>;
  const cards = [
    ...data.map((g) => ({
      key: g.slug, href: `/events/${encodeURIComponent(g.slug)}`, title: g.title, sub: g.eventDate,
      cover: g.photos[0] ? `${API_BASE}${g.photos[0].heroUrl}?w=800` : null,
      meta: g.bookingOpen ? `Booking open${g.priceCents ? ` · $${Math.round(g.priceCents / 100)}` : ''}` : g.signupOpen ? 'Sign-ups open' : `${g.photos.length} photo${g.photos.length === 1 ? '' : 's'}`,
    })),
    ...(Array.isArray(paws) && paws.length ? [{
      key: 'paws', href: '/paws-gallery', title: 'Paws & Portraits', sub: 'July 26 & August 9',
      cover: `${API_BASE}${paws[0].heroUrl}?w=800`, meta: `${paws.length} pets`,
    }] : []),
  ];
  return (
    <div style={{ background: c.paper, minHeight: '100vh' }}>
      <Masthead title="Events" subtitle="Photographed by HDDN LAB · New Rochelle, NY" />
      <div className="eg-layout" style={{ gridTemplateColumns: '1fr' }}>
        <main>
          {!cards.length && <p style={{ fontFamily: sans, fontSize: 14, color: c.muted }}>Nothing here yet.</p>}
          <div className="eg-cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {cards.map((k) => (
              <a key={k.key} href={k.href} onClick={link(k.href)} style={{ textDecoration: 'none', border: `1px solid ${c.sand}`, background: c.paper, display: 'block' }}>
                {k.cover
                  ? <img src={k.cover} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', aspectRatio: '4 / 3', background: c.cream }} />}
                <div style={{ padding: '16px 16px 18px' }}>
                  <div style={{ fontFamily: sans, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: c.ink }}>{k.title}</div>
                  <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: c.orange, marginTop: 8 }}>{k.meta}{k.sub ? ` · ${k.sub}` : ''}</div>
                </div>
              </a>
            ))}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default function EventGallery({ event, slug }) {
  if (!event) return <EventsIndex />;
  if (slug === 'signup') return <SignupPage event={event} />;
  if (slug === 'book') return <BookPage event={event} />;
  return slug ? <GalleryEntry event={event} slug={slug} /> : <GalleryIndex event={event} />;
}
